import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Moon, Heart } from "lucide-react";
import { primeStoryAudio } from "@/lib/storyAudio";
import { api, errMsg } from "@/lib/api";
import { BreathCircle } from "@/components/BreathCircle";
import { JournalMic } from "@/components/journal/JournalMic";
import { StoryStep } from "@/components/bedtime/StoryStep";
import { BedtimeReminder } from "@/components/bedtime/BedtimeReminder";

const STEPS = ["worry", "gratitude", "breathe", "story"];
const NIGHT = "#141A33", AMBER = "#F3C98B";
const btn = "rounded-full font-semibold py-3.5 px-6";

const TextStep = ({ title, hint, placeholder, value, onChange, testId }) => (
  <div className="w-full max-w-md" data-testid={`bedtime-${testId}-step`}>
    <h2 className="text-2xl font-semibold mb-1">{title}</h2>
    <p className="opacity-70 text-sm mb-5">{hint}</p>
    <textarea value={value} onChange={(e) => onChange(e.target.value.slice(0, 280))} rows={3} placeholder={placeholder}
      className="w-full rounded-2xl bg-white/5 border border-white/15 p-4 text-lg placeholder:text-white/30 focus:outline-none focus:border-[#F3C98B]" data-testid={`bedtime-${testId}-input`} />
    <div className="mt-3 [&_button]:!text-white [&_button]:!border-white/20"><JournalMic onText={(t) => onChange(`${value ? `${value} ` : ""}${t}`.slice(0, 280))} /></div>
  </div>
);

const Intro = ({ onStart, favorites, onFavorite }) => (
  <div className="text-center max-w-md" data-testid="bedtime-intro">
    <Moon size={44} className="mx-auto mb-4" style={{ color: AMBER }} />
    <h1 className="text-3xl sm:text-4xl font-semibold mb-3">Time to wind down</h1>
    <p className="opacity-70 mb-8">About 5 calm minutes: park tomorrow's worry, one good thing, a minute of slow breathing, then a sleep story. Skip anything you like.</p>
    <button onClick={onStart} className={`${btn} w-full`} style={{ background: AMBER, color: NIGHT }} data-testid="bedtime-start">Begin</button>
    {favorites > 0 && <button onClick={onFavorite} className={`${btn} w-full mt-3 bg-white/10 flex items-center justify-center gap-2`} data-testid="bedtime-play-favorite"><Heart size={16} className="fill-[#F28FAD] text-[#F28FAD]" />Play my favorite story</button>}
    <div className="mt-8"><BedtimeReminder /></div>
  </div>
);

export default function Bedtime() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [autoFav, setAutoFav] = useState(params.get("play") === "favorite");
  const [i, setI] = useState(autoFav ? 3 : -1);
  const [worry, setWorry] = useState("");
  const [good, setGood] = useState("");
  const [did, setDid] = useState([]);
  const [storyId, setStoryId] = useState(null);
  const [status, setStatus] = useState(null);
  const [night, setNight] = useState(false);
  useEffect(() => { api.get("/bedtime/status", { params: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone } }).then((r) => setStatus(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (autoFav && status && !status.favorites) { setAutoFav(false); setI(-1); } }, [autoFav, status]);
  const step = STEPS[i];
  const mark = (s) => setDid((d) => (d.includes(s) ? d : [...d, s]));
  const next = async () => {
    try {
      if (step === "worry" && worry.trim()) { await api.post("/worry", { text: `Tomorrow: ${worry.trim()}`.slice(0, 300), know_on: new Date(Date.now() + 864e5).toLocaleDateString("en-CA") }); mark("worry"); toast.success("Parked in your Worry Box until tomorrow"); }
      if (step === "gratitude" && good.trim()) { await api.post("/gratitude", { text: good.trim(), emoji: "🌙" }); mark("gratitude"); toast.success("Added to your Gratitude Jar"); }
    } catch (e) { toast.error(errMsg(e)); return; }
    setI(i + 1);
  };
  const breathed = useCallback(() => { mark("breathe"); setI(3); }, []);
  const played = useCallback((id) => { mark("story"); setStoryId(id); }, []);
  const goodnight = async () => {
    try { await api.post("/bedtime/done", { steps: did, story_id: storyId }); } catch (e) { toast.error(errMsg(e)); }
    setNight(true);
  };
  const exit = () => (window.history.length > 1 ? nav(-1) : nav("/"));
  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-[2000ms] ${night ? "bg-black" : ""}`} style={{ background: night ? "#05060d" : NIGHT, color: "#F4E9D8" }} data-testid="bedtime-page">
      <div className="flex justify-between items-center p-4">
        <div className="flex gap-1.5" aria-hidden>{STEPS.map((s, k) => <span key={s} className="h-1.5 w-8 rounded-full" style={{ background: k <= i ? AMBER : "rgba(255,255,255,.15)" }} />)}</div>
        <button onClick={exit} className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center" aria-label="Leave wind-down" data-testid="bedtime-exit"><X size={20} /></button>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-16 mm-fade-up" key={i}>
        {i === -1 && <Intro onStart={() => setI(0)} favorites={status?.favorites} onFavorite={() => { primeStoryAudio(); setAutoFav(true); setI(3); }} />}
        {step === "worry" && <TextStep testId="worry" title="Park tomorrow's worry" hint="Write or say one thing on your mind. It goes in your Worry Box for tomorrow, so it's out of your head tonight." placeholder="Tomorrow I need to…" value={worry} onChange={setWorry} />}
        {step === "gratitude" && <TextStep testId="gratitude" title="One good thing" hint="Something small from today that you're glad about. It goes in your Gratitude Jar." placeholder="Today I'm glad that…" value={good} onChange={setGood} />}
        {step === "breathe" && <><h2 className="text-2xl font-semibold">Slow breathing</h2><BreathCircle seconds={60} onDone={breathed} testId="bedtime-breathe" /></>}
        {step === "story" && <StoryStep onPlayed={played} storiesLeft={status?.stories_left} autoFavorite={autoFav} />}
        {night && <p className="mt-8 text-lg opacity-80 text-center" data-testid="bedtime-goodnight-msg">Sleep well 🌙 You can turn your screen off. The story keeps playing.</p>}
      </div>
      {i >= 0 && !night && (
        <div className="flex justify-center gap-3 px-6 pb-10">
          {step !== "story" && <button onClick={() => setI(i + 1)} className={`${btn} bg-white/10`} data-testid="bedtime-skip">Skip</button>}
          {(step === "worry" || step === "gratitude") && <button onClick={next} className={btn} style={{ background: AMBER, color: NIGHT }} data-testid="bedtime-next">Next</button>}
          {step === "story" && <button onClick={goodnight} className={btn} style={{ background: AMBER, color: NIGHT }} data-testid="bedtime-goodnight">Goodnight</button>}
        </div>
      )}
    </div>
  );
}
